import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { getArticleById, type FailArticle } from "@/mocks/articles";
import TimelineBlock from "./components/TimelineBlock";
import ScreenshotGallery from "./components/ScreenshotGallery";
import ReactionBlock from "./components/ReactionBlock";
import ArticleSidebar from "./components/ArticleSidebar";
import RelatedList from "./components/RelatedList";

function ArticleNotFound() {
  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 py-20 md:py-28 bg-background-50">
        <div className="max-w-xl mx-auto text-center">
          <span className="inline-flex w-14 h-14 rounded-full bg-background-100 border border-background-200 items-center justify-center text-foreground-500 mb-5">
            <i className="ri-file-warning-line text-2xl"></i>
          </span>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">这篇事件整理不存在</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600">
            链接可能已经失效，或者事件整理仍在编辑中。你可以回到事件列表继续浏览。
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              浏览全部事件
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md border border-background-300 text-sm text-foreground-800 whitespace-nowrap hover:border-primary-400 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article: FailArticle | undefined = getArticleById(id);

  const crumbs = article
    ? [
        { label: "首页", to: "/" },
        { label: "全部事件", to: "/category" },
        { label: article.category, to: `/category?cat=${article.categorySlug}` },
        { label: article.title },
      ]
    : [{ label: "首页", to: "/" }, { label: "全部事件", to: "/category" }, { label: "内容不存在" }];

  usePageMeta({
    title: article ? `${article.title} - 直播翻车观察局` : "事件整理不存在 - 直播翻车观察局",
    description: article
      ? article.excerpt
      : "该事件整理不存在或链接已失效，你可以回到事件列表继续浏览其他网红翻车现场与名场面回放。",
    keywords: article
      ? Array.from(new Set([...article.tags, article.category, "网红翻车", "网红翻车现场"])).join(",")
      : "网红翻车,网红翻车现场,翻车事件列表",
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: article ? article.title : "事件整理不存在",
          description: article ? article.excerpt : "该事件整理不存在或链接已失效。",
          articleSection: article ? article.category : "事件列表",
          keywords: article ? article.tags.join(",") : "网红翻车",
          datePublished: article ? article.date : "",
          dateModified: article ? article.date : "",
          inLanguage: "zh-CN",
          author: { "@type": "Organization", name: "直播翻车观察局" },
          publisher: { "@type": "Organization", name: "直播翻车观察局" },
          mainEntityOfPage: `https://fanche-observer.example/article/${id}`,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.label,
            item: crumb.to ? `https://fanche-observer.example${crumb.to}` : undefined,
          })),
        },
      ],
    },
  });

  if (!article) {
    return <ArticleNotFound />;
  }

  return (
    <SiteLayout>
      <article>
        <header className="w-full px-4 md:px-8 pt-8 pb-6 bg-background-100 border-b border-background-200">
          <Breadcrumb items={crumbs} />
          <div className="mt-6 max-w-4xl">
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <Link
                to={`/category?cat=${article.categorySlug}`}
                className="inline-flex items-center px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs whitespace-nowrap"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-500">阅读 {article.views}</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h1 className="font-heading font-black text-2xl md:text-4xl lg:text-[2.75rem] leading-tight text-foreground-950">
              {article.title}
            </h1>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">{article.excerpt}</p>
          </div>
        </header>

        <div className="w-full px-4 md:px-8 py-8 md:py-10 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            <div className="lg:col-span-2 space-y-10 md:space-y-12">
              <div className="rounded-lg overflow-hidden bg-background-200">
                <img
                  src={article.cover}
                  alt={`${article.title} 配图`}
                  className="w-full aspect-[16/9] object-cover object-top"
                />
              </div>

              <div className="border-l-4 border-primary-500 bg-background-100 px-5 py-4 rounded-r-lg">
                <p className="font-heading text-base md:text-lg leading-relaxed text-foreground-800">
                  {article.lead}
                </p>
              </div>

              <TimelineBlock items={article.timeline} />

              <ScreenshotGallery items={article.screenshots} />

              <section aria-labelledby="body-title" className="w-full">
                <h2 id="body-title" className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className="ri-article-line"></i>
                  </span>
                  事件复盘
                </h2>
                <div className="space-y-8 mt-6">
                  {article.sections.map((section, index) => (
                    <div key={section.heading}>
                      <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground-950 mb-3">
                        <span className="text-primary-500 mr-2">0{index + 1}</span>
                        {section.heading}
                      </h3>
                      <div className="space-y-3">
                        {section.paragraphs.map((paragraph) => (
                          <p key={paragraph.slice(0, 18)} className="text-sm md:text-base leading-relaxed text-foreground-700">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <figure className="mt-8 p-5 md:p-6 rounded-lg bg-foreground-950">
                  <blockquote className="font-heading text-base md:text-xl text-background-50 leading-relaxed">
                    <i className="ri-double-quotes-l mr-1 text-accent-400"></i>
                    {article.quote.replace(/[「」]/g, "")}
                  </blockquote>
                  <figcaption className="mt-3 text-xs text-background-300">
                    —— 编辑在复盘时最想强调的一句
                  </figcaption>
                </figure>
              </section>

              <ReactionBlock items={article.reactions} />

              <div className="pt-6 border-t border-background-200 flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">这篇整理有帮助吗？</span>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-xs whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-thumb-up-line"></i>
                  提供补充信息
                </Link>
                <Link
                  to={`/category?cat=${article.categorySlug}`}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-background-300 text-xs text-foreground-700 whitespace-nowrap hover:border-primary-400 transition-colors"
                >
                  <i className="ri-links-line"></i>
                  查看更多{article.category}
                </Link>
              </div>
            </div>

            <div className="lg:col-span-1">
              <ArticleSidebar article={article} />
            </div>
          </div>
        </div>
      </article>

      <RelatedList currentId={article.id} />
    </SiteLayout>
  );
}